import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {MagnifyingGlass, Plus} from '../../icons';
import {fetchAgencyClients} from '../api';

/**
 * A quién pertenece esta cuenta.
 *
 * Es la interacción central del trabajo manual del paso 3, así que está pensada para resolverse sin
 * salir de la fila: se abre al lado de la cuenta, se escribe, se elige y se cierra. Un modal para
 * cada cuenta convertiría veinte asignaciones en veinte interrupciones.
 *
 * Ofrece primero los clientes que se están armando en este mismo recorrido y después los que la
 * agencia ya tenía. Ese orden no es alfabético a propósito: lo más probable es que la cuenta huérfana
 * pertenezca a alguno de los clientes que el usuario acaba de ver pasar.
 */
export function AssignToPicker({groups, defaultName, onPick, onClose}) {
    const {t} = useTranslation();
    const [query, setQuery] = useState('');
    const [agencyClients, setAgencyClients] = useState([]);
    const container = useRef(null);
    const close = useRef(onClose);
    close.current = onClose;

    useEffect(() => {
        const onClickOutside = event => {
            if (container.current && !container.current.contains(event.target)) close.current();
        };
        const onEscape = event => {
            if (event.key === 'Escape') close.current();
        };
        document.addEventListener('mousedown', onClickOutside);
        document.addEventListener('keydown', onEscape);
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
            document.removeEventListener('keydown', onEscape);
        };
        // Sin dependencias a propósito: `onClose` llega como función nueva en cada render del padre
        // y volvería a registrar los dos escuchas en cada uno.
    }, []);

    // Los clientes que la agencia ya tiene. Sin esto, una cuenta que pertenece a un cliente
    // existente sólo se podía resolver creando otro con el mismo nombre — y el alta lo rechaza.
    useEffect(() => {
        fetchAgencyClients().then(response => {
            if (response?.status === 'OK') setAgencyClients(clientsFrom(response.data));
        });
    }, []);

    const {inProgress, existing} = useMemo(
        () => splitGroups(groups, agencyClients, query), [groups, agencyClients, query]);
    const newName = query.trim() || defaultName;

    return (
        <div className="ob-picker" ref={container} role="dialog"
             aria-label={t('onboarding.clients.assign_title')}>
            <div className="ob-picker__search">
                <MagnifyingGlass size={14}/>
                <input autoFocus value={query} onChange={event => setQuery(event.target.value)}
                       placeholder={t('onboarding.clients.assign_search')}/>
            </div>

            <div className="ob-picker__list">
                {inProgress.length > 0 && (
                    <>
                        <p className="ob-picker__group">{t('onboarding.clients.assign_in_progress')}</p>
                        {inProgress.map(group => (
                            <button type="button" key={group.key} className="ob-picker__item"
                                    onClick={() => onPick(group.clientRef)}>
                                {group.name}
                            </button>
                        ))}
                    </>
                )}

                {existing.length > 0 && (
                    <>
                        <p className="ob-picker__group">{t('onboarding.clients.assign_existing')}</p>
                        {existing.map(group => (
                            <button type="button" key={group.key} className="ob-picker__item"
                                    onClick={() => onPick(group.clientRef)}>
                                {group.name}
                            </button>
                        ))}
                    </>
                )}

                {inProgress.length === 0 && existing.length === 0 && (
                    <p className="ob-picker__empty">{t('onboarding.clients.assign_no_matches')}</p>
                )}
            </div>

            {newName && (
                <button type="button" className="ob-picker__create"
                        onClick={() => onPick({type: 'new', name: newName})}>
                    <Plus size={13}/>
                    <span>{t('onboarding.clients.assign_create', {name: newName,
                                                                  interpolation: {escapeValue: false}})}</span>
                </button>
            )}
        </div>
    );
}

function splitGroups(groups, agencyClients, query) {
    const needle = query.trim().toLowerCase();
    const matches = item => !needle || (item.name || '').toLowerCase().includes(needle);

    const inProgress = groups.filter(group => group.isNew).filter(matches);
    // Los clientes reales de la agencia, más los que ya aparecen en el armado. Se deduplica por id
    // para no ofrecer dos veces el mismo destino.
    const seen = new Set();
    const existing = [];
    groups.filter(group => !group.isNew).concat(agencyClients).forEach(item => {
        const id = item.clientRef?.id ?? item.id;
        if (id === undefined || seen.has(id)) return;
        seen.add(id);
        existing.push({key: `existing:${id}`, name: item.name,
                       clientRef: item.clientRef || {type: 'existing', id}});
    });

    return {inProgress, existing: existing.filter(matches)};
}

/** `agency/clients/` cambió de forma más de una vez; se toleran las dos que están en producción. */
function clientsFrom(data) {
    const list = Array.isArray(data) ? data : (data?.agency_clients || data?.clients || []);
    return list.filter(client => client && client.id).map(client => ({id: client.id, name: client.name}));
}
